import {
  Component,
  Element,
  h,
  Host,
  Listen,
  Method,
  Prop,
  State,
  Watch,
} from "@stencil/core";
import {
  getVisibleFocusables,
  startWatchingFocusOutAndClick,
} from "@globals/helpers/focus-helpers";
import { FlyoutProps } from "./f-flyout-model";
import classNames from "classnames";
import { generateRandomNumber } from "@globals/random-number-helpers";

@Component({
  tag: "f-flyout",
  styleUrl: "f-flyout.scss",
  shadow: true,
})
export class Flyout {
  @Element() el: HTMLFFlyoutElement;
  @Prop() flyoutTitle: FlyoutProps["flyoutTitle"] = "";
  @Prop() closeButtonHiddenLabel: FlyoutProps["closeButtonHiddenLabel"] = (
    window as any
  ).translations["bcp-acc"].close;
  @Prop({ mutable: true }) attachedTrigger: FlyoutProps["attachedTrigger"];
  @Prop() flyoutId: FlyoutProps["flyoutId"] =
    "f-flyout-" + generateRandomNumber();
  @Prop() flyoutDataTest: FlyoutProps["flyoutDataTest"] = "flyout";
  @Prop() variant: FlyoutProps["variant"] = "default";

  @Prop() ttHideHeader: FlyoutProps["ttHideHeader"] = false; //TT CUSTOM

  private container: HTMLDivElement;
  private trigger: HTMLElement;
  private closeFlyoutController: AbortController;
  private positioningElement: HTMLElement;
  private className = "f-flyout";

  @State() isOpen = false;
  @State() isOpening = false;

  @Watch("attachedTrigger")
  checkTriggerPosition() {
    this.positioningElement = this.attachedTrigger;
    this.recalculatePosition();
  }

  componentWillLoad() {
    // this is an attempt to make the flyout move less on first load
    this.positioningElement = this.attachedTrigger;
    this.recalculatePosition();
  }

  componentWillRender() {
    document.body.append(this.el);
  }

  componentDidRender() {
    if (this.isOpening || this.isOpen) {
      this.recalculatePosition();
    }
  }

  @Method()
  async openFlyout(element, focusOnFlyout = true) {
    if (!this.isOpen) {
      this.trigger = element || this.attachedTrigger;
      this.isOpening = true;
      setTimeout(() => {
        this.isOpen = true;
        this.isOpening = false;
        // timeout is needed because without it the flyout closes instantly (event propagates and is already caught by a listener from startWatchingFocusOutAndClick)
        setTimeout(() => {
          this.closeFlyoutController = new AbortController();
          startWatchingFocusOutAndClick(
            this.el,
            this.closeFlyoutController,
            this.closeFlyout.bind(this)
          );
          // focus doesn't work when the flyout is opened for the first time without setTimeout
          if (focusOnFlyout) {
            let focusables = getVisibleFocusables(this.el);
            if (focusables && focusables.length > 0) {
              focusables[0].focus();
            } else {
              let iterator = 0;
              const interval = setInterval(() => {
                iterator++;
                focusables = getVisibleFocusables(this.el);
                if ((focusables && focusables.length > 0) || iterator === 20) {
                  focusables[0].focus();
                  window.clearInterval(interval);
                }
              }, 10);
            }
          }
        }, 0);
      }, 0);
    }
  }

  @Method()
  async toggleFlyout(element?) {
    if (!this.isOpen) {
      await this.openFlyout(element);
    } else {
      this.closeFlyout();
    }
  }

  @Listen("openFlyout", { target: "document" })
  toggleFlyoutFromOutside(eventDetails) {
    if (this.flyoutId === eventDetails.detail.id) {
      if (eventDetails.detail.setTrigger) {
        this.positioningElement = eventDetails.detail.trigger;
      }
      this.toggleFlyout(eventDetails.detail.trigger);
    }
  }

  getAssumedWidth = () => {
    return document.body.clientWidth > 766 ? 360 : document.body.clientWidth;
  };

  @Method()
  async closeFlyout(moveFocus = true) {
    this.isOpen = false;
    this.closeFlyoutController?.abort();
    this.positioningElement = this.attachedTrigger;

    if (moveFocus) {
      // getVisibleFocusables is only needed because of shadow DOM, without it setting focus on trigger would be enough
      getVisibleFocusables(this.trigger)[0]?.focus({
        preventScroll: true,
      });
      this.trigger?.focus({
        preventScroll: true,
      });
    }
  }

  @Listen("resize", { target: "window" })
  private recalculatePosition() {
    setTimeout(() => {
      if (!!this.positioningElement) {
        const triggerPosition = this.positioningElement.getBoundingClientRect();
        const containerPosition = this.container.getBoundingClientRect();

        const pageOffset = window.scrollX;
        const pageOffsetY = window.scrollY;
        const documentWidth = document.body.clientWidth;

        // width of l-page-row
        const pageContainerWidth = Math.min(documentWidth, 1152);

        const triggerLeftPosition = triggerPosition.left + pageOffset;
        const triggerRightPosition = triggerPosition.right + pageOffset;
        const triggerBottomPosition = triggerPosition.bottom + pageOffsetY;
        const middleOfTheTrigger =
          (triggerRightPosition - triggerLeftPosition) / 2;

        const containerWidth =
          containerPosition.width || this.getAssumedWidth();
        const sizeOfTheTriangleHorizontal = 31.11;
        const halfOfSizeOfTheTriangleHorizontal = 15.55;
        const halfOfSizeOfTheTriangleVertical = 5;

        const containerTopPosition =
          triggerBottomPosition + 2 * halfOfSizeOfTheTriangleVertical;
        this.el.style.setProperty(
          "--flyout-container-top",
          `${containerTopPosition}px`
        );

        let containerRightPosition;
        const defaultRightPosition = (documentWidth - pageContainerWidth) / 2;
        if (
          documentWidth - defaultRightPosition - containerWidth <
          triggerLeftPosition
        ) {
          containerRightPosition = defaultRightPosition;
        } else {
          containerRightPosition =
            documentWidth -
            triggerLeftPosition -
            containerWidth +
            sizeOfTheTriangleHorizontal;
        }
        this.el.style.setProperty(
          "--flyout-container-right",
          `${containerRightPosition}px`
        );

        const tipRightPosition =
          documentWidth -
          triggerRightPosition +
          middleOfTheTrigger -
          halfOfSizeOfTheTriangleHorizontal -
          containerRightPosition;
        this.el.style.setProperty(
          "--flyout-tip-right",
          `${tipRightPosition}px`
        );
      }
    }, 0);
  }

  render() {
    const flyoutClasses = classNames(this.className, {
      [`${this.className}--only-content`]: this.variant === "onlyContent",
    });

    return (
      <Host
        id={this.flyoutId}
        data-test={this.flyoutDataTest}
        class={this.variant === "onlyContent" && "only-content"}
      >
        <div
          class={`${flyoutClasses} ${this.isOpen ? "is-open" : ""} ${
            this.isOpening ? "is-opening" : ""
          }`}
          ref={(el) => {
            this.container = el;
          }}
          data-test="flyout"
        >
          <div class="f-flyout__inner">
            <div class="f-flyout__box">
              <div
                class={`f-flyout__header ${
                  this.ttHideHeader ? "tt-hide-header" : ""
                }`} //TT CUSTOM
                data-test={`${this.flyoutDataTest}-header`}
              >
                {!Boolean(this.variant === "onlyContent") && (
                  <h2
                    class="f-flyout__heading"
                    data-test={`${this.flyoutDataTest}-header-title`}
                  >
                    {this.flyoutTitle}
                  </h2>
                )}
                <div class="f-flyout__close" data-test="flyout-header-close">
                  <f-button
                    variant="integrated"
                    iconOnly={true}
                    onClick={() => this.closeFlyout()}
                    data-test={`${this.flyoutDataTest}-header-btn-close`}
                  >
                    <f-visually-hidden>
                      {this.closeButtonHiddenLabel}
                    </f-visually-hidden>
                    <f-icon
                      iconName="close"
                      data-test={`${this.flyoutDataTest}-header-icon-close`}
                    />
                  </f-button>
                </div>
              </div>
              <div
                class="f-flyout__feature-bar"
                data-test={`${this.flyoutDataTest}-feature-bar-slot`}
              >
                <slot name="flyout-feature-bar" />
              </div>
              <div
                class="f-flyout__content"
                data-test={`${this.flyoutDataTest}-content-slot`}
              >
                <slot name="flyout-content" />
              </div>
              <div
                class="f-flyout__footer"
                data-test={`${this.flyoutDataTest}-footer-slot`}
              >
                <slot name="flyout-footer" />
              </div>
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
